import React from 'react';
import {
  ACTIONS, EVENTS, STATUS, useJoyride,
} from 'react-joyride';

import { useAuthDataContext } from 'components/AuthDataProvider/AuthDataProvider';
import TourTooltip from 'components/GuidedTour/TourTooltip';
import useTourAppearance from 'components/GuidedTour/useTourAppearance';

import { fetchTutorial, saveTutorial } from 'utils/api/tutorials';

const steps = [
  {
    target: '[data-tour-id="dashboard-actions"]',
    placement: 'bottom-start',
    title: 'Start with your next conversation',
    content: 'Your action items highlight prospects to follow up with. Open an item to see their activity and a suggested next step, then mark it done after you follow up.',
  },
  {
    target: '[data-tour-id="dashboard-farm"]',
    placement: 'bottom-start',
    title: 'Get to know your farm area',
    content: 'Explore the market in your farm ZIP codes. Switch ZIPs to compare areas and use the information buttons to understand the estimates.',
  },
  {
    target: '[data-tour-id="dashboard-engagement"]',
    scrollTarget: '[data-tour-id="dashboard-engagement-panel"]',
    placement: 'bottom-start',
    title: 'See how your outreach is doing',
    content: 'Track engagement over the last 90 days and see how your brand is reaching prospects across channels.',
  },
  {
    target: '[data-tour-id="dashboard-upcoming"]',
    placement: 'bottom-start',
    title: 'Know what’s coming next',
    content: 'See your scheduled touches for the next two weeks. Open a campaign to review its details. You can replay this walkthrough with “Show dashboard tour” at the top of the page.',
  },
];

// Each run has its own lifecycle; replays never reset the account's saved progress.
const TourRun = ({ tourSteps, onEnd, onError }) => {
  const appearance = useTourAppearance();
  const ended = React.useRef(false);
  const { Tour } = useJoyride({
    ...appearance,
    steps: tourSteps,
    run: true,
    continuous: true,
    tooltipComponent: TourTooltip,
    locale: { last: 'Done', skip: 'Skip tour' },
    options: {
      ...appearance.options,
      skipBeacon: true,
      overlayClickAction: false,
      closeButtonAction: 'skip',
      blockTargetInteraction: true,
      skipScroll: true,
      before: async ({ step }) => {
        // Native scrolling handles the dashboard's overflow wrapper even when
        // the document, rather than that wrapper, is the actual scroll container.
        // Finish scrolling before Joyride measures and focuses the tooltip.
        document.querySelector(step.scrollTarget || step.target)?.scrollIntoView({
          behavior: 'instant', block: 'start', inline: 'nearest',
        });
      },
    },
    onEvent: ({ type, status, action }) => {
      if (ended.current) return;
      if (type.startsWith('error:')) {
        ended.current = true;
        onError();
      } else if (action === ACTIONS.CLOSE || (type === EVENTS.TOUR_END && status === STATUS.SKIPPED)) {
        // Escape dismisses the walkthrough instead of accidentally completing its last step.
        ended.current = true;
        onEnd('dismissed');
      } else if (type === EVENTS.TOUR_END && status === STATUS.FINISHED) {
        ended.current = true;
        onEnd('completed');
      }
    },
  });
  return Tour;
};

const AccountTour = () => {
  const [tourSteps, setTourSteps] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const requests = React.useRef(null);

  const start = React.useCallback(() => {
    // Empty-state sections vary by account. Build a stable list from the mounted dashboard.
    const available = steps.filter((step) => document.querySelector(step.target));
    if (!available.length) return;
    setMessage('');
    setTourSteps(available);
  }, []);

  React.useEffect(() => {
    const controller = new AbortController();
    requests.current = controller;
    fetchTutorial('agent-dashboard', controller.signal).then((state) => {
      if (controller.signal.aborted) return;
      setLoading(false);
      if (state.version === 1 && !state.completedAt && !state.dismissedAt) start();
    }).catch(() => {
      if (controller.signal.aborted) return;
      setLoading(false);
      setMessage('We couldn’t load your tour progress. You can still start the tour.');
    });
    return () => controller.abort();
  }, [start]);

  const save = async (status) => {
    const signal = requests.current?.signal;
    if (!signal || signal.aborted) return;
    setTourSteps(null);
    setSaving(true);
    try {
      await saveTutorial('agent-dashboard', status, signal);
      if (signal.aborted) return;
      setMessage('');
    } catch (error) {
      // Saving is best-effort; an unsaved tour can appear again after a refresh.
    } finally {
      if (!signal.aborted) setSaving(false);
    }
  };

  return (
    <div className="ah-tour-controls">
      <button type="button" className="ah-text-button" onClick={start} disabled={loading || saving || !!tourSteps}>
        Show dashboard tour
      </button>
      {message && <span className="ah-tour-message" role="status">{message}</span>}
      {tourSteps && (
        <TourRun
          tourSteps={tourSteps}
          onEnd={save}
          onError={() => {
            setTourSteps(null);
            setMessage('The tour couldn’t show a section. Please try starting it again.');
          }}
        />
      )}
    </div>
  );
};

const AgentDashboardTour = () => {
  const auth = useAuthDataContext();
  // Do not mount the tour, fetch its state, or expose replay while impersonating.
  if (!auth?.isAuthLoaded || !auth.isAnyAgent || !auth.user?.id || auth.user.isAssumedUser) return null;
  return <AccountTour key={auth.user.id} />;
};

export default AgentDashboardTour;
